import throttle from 'lodash.throttle';
import React from 'react';
import { FlexRow } from '../../../../common/components/v2';
import { DocumentType } from '../../../../common/constants/documentType';
import { Resource } from '../../../../common/constants/resource';
import { TaskState } from '../../../../common/constants/states';
import {
  EvidenceStateInfo,
  HELD_ERROR,
  ITask,
} from '../../../tasks/interfaces/ITask';
import { TaskTypes } from '../../../tasks/types/taskTypes';
import styles from '../../styles.module.scss';
import {
  evidenceTaskHasRetryOption,
  evidenceTaskHasUploadOption,
} from '../../utilities/taskDetailUtilities';
import { FileDetail } from './FileDetail';
import { FileUpload } from './FileUpload';
import { TaskDetailsTitledItem } from './TaskDetailsTitledItem';

export const RetryableEvidenceItem = ({
  task,
  documentType,
  title,
  children,
  uploadText,
  retryHandler,
  markOutdatedHandler,
  uploadHandler,
  downloadHandler,
  taskStateHandler,
  deleteHandler,
  canDeleteFile = true,
  renderChildrenWhenFilesExist = false,
}: {
  task: ITask;
  documentType: DocumentType;
  title: string;
  children?: React.ReactNode;
  uploadText?: string;
  retryHandler?: (taskId: number) => void;
  markOutdatedHandler?: (taskId: number) => void;
  uploadHandler: (
    existingTaskData: ITask,
    data: FormData,
    type: string,
  ) => void;
  downloadHandler: (uri: string, fileName: string) => void;
  deleteHandler: (
    existingTaskData: ITask,
    documentId: number,
    reason: string,
  ) => void;
  taskStateHandler: (taskId: number, state: TaskState, reason?: string) => void;
  canDeleteFile?: boolean;
  renderChildrenWhenFilesExist?: boolean;
}) => {
  const hasRetryOption = evidenceTaskHasRetryOption(task);
  const showUpload = evidenceTaskHasUploadOption(task);
  const hasFiles = Boolean(task?.evidence?.documents?.length);

  const onRetry = throttle(
    () => {
      if (retryHandler) {
        retryHandler(task.id);
      }
    },
    5000,
    { leading: true, trailing: false },
  );

  const onMarkOutdated = throttle(
    () => {
      if (markOutdatedHandler) {
        markOutdatedHandler(task.id);
      }
    },
    5000,
    { leading: true, trailing: false },
  );

  const onFileSelection = (file: any, type: string) => {
    uploadHandler(task, file, type);
  };

  return (
    <TaskDetailsTitledItem
      task={task}
      title={title}
      taskStateHandler={taskStateHandler}
      retryHandler={hasRetryOption ? onRetry : undefined}
      markOutdatedHandler={markOutdatedHandler ? onMarkOutdated : undefined}
    >
      {RenderHeldError()}
      {hasFiles && (
        <FileDetail
          documents={task?.evidence?.documents || []}
          displayString={title}
          fileOpenHandler={downloadHandler}
          fileDeletehandler={(documentId: number, reason: string) =>
            deleteHandler(task, documentId, reason)
          }
          canDeleteFile={canDeleteFile}
        />
      )}
      {showUpload && (
        <FileUpload
          leftItem={Resource.FILES}
          selectedFiles={onFileSelection}
          type={documentType}
          uploadText={uploadText}
          multiple={false}
          ongoingUpload={task.ongoingUpload?.[documentType]}
        />
      )}
      {RenderEvidenceError()}
      {!hasFiles || renderChildrenWhenFilesExist ? children : <></>}
    </TaskDetailsTitledItem>
  );

  function RenderHeldError() {
    return (
      <FlexRow className={styles.MarginTop}>
        {task.taskType === TaskTypes.MVR &&
          task.evidenceStateInfo === EvidenceStateInfo.HELD &&
          HELD_ERROR}
      </FlexRow>
    );
  }

  function RenderEvidenceError() {
    if (task.evidence?.errorMessage) {
      const messages = task.evidence?.errorMessage.split('\n');
      return (
        <FlexRow className={styles.MarginTop}>
          <ul>
            {messages.map(m => (
              <li>{m}</li>
            ))}
          </ul>
        </FlexRow>
      );
    }
  }
};
